import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Sparky } from "../components/Sparky";
import { api } from "../lib/api";
import { LogOut, Sparkles, PlayCircle, BookOpen, Receipt, CheckCircle2, XCircle, Clock, GraduationCap, Download, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [orders, setOrders] = useState(null);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [certs, setCerts] = useState(null);

  useEffect(() => {
    api.myEnrollments().then(setRows).catch(() => setRows([]));
    api.listOrders().then(setOrders).catch(() => setOrders([]));
    api.myCertificates().then(setCerts).catch(() => setCerts([]));
  }, []);

  async function onLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="dashboard-page">
      <Header />
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-10 md:py-14 space-y-10">
        {/* Welcome card */}
        <div className="rounded-[2.5rem] bg-white border-2 border-black/5 p-8 md:p-12 shadow-[0_20px_60px_rgba(0,0,0,0.06)] relative overflow-hidden">
          <div aria-hidden className="absolute -top-24 -end-24 h-64 w-64 rounded-full blur-3xl opacity-40" style={{ background: "#4CC9F0" }} />
          <div aria-hidden className="absolute -bottom-24 -start-24 h-64 w-64 rounded-full blur-3xl opacity-40" style={{ background: "#F72585" }} />

          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="animate-float">
              <Sparky size={140} />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3C4] text-[#7A4B00] px-3 py-1 text-xs font-black">
                <Sparkles className="h-3.5 w-3.5" /> {user?.role?.toUpperCase() || "STUDENT"}
              </div>
              <h1
                className="mt-3 text-4xl md:text-5xl font-black tracking-tight"
                data-testid="dashboard-welcome"
              >
                {t("dashboard.welcome")}, {user?.name} 👋
              </h1>
              <p className="mt-3 text-lg text-black/70 font-medium max-w-xl">
                {t("dashboard.body")}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-bold px-6 py-3 shadow-[0_3px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_2px_0_#2A3EB1] transition"
                  data-testid="dashboard-browse-btn"
                >
                  <BookOpen className="h-4 w-4" /> {t("dashboard.browseAll")}
                </Link>
                <button
                  onClick={onLogout}
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 hover:border-black/30 text-black/80 font-bold px-5 py-3 transition"
                  data-testid="dashboard-logout-btn"
                >
                  <LogOut className="h-4 w-4" /> {t("dashboard.logout")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* My Courses */}
        <section>
          <h2 className="text-2xl md:text-3xl font-black tracking-tight">{t("dashboard.myCourses")}</h2>
          <div className="mt-6" data-testid="my-courses">
            {rows === null ? (
              <div className="grid md:grid-cols-2 gap-5">
                {[0, 1].map((i) => (
                  <div key={i} className="h-36 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-3xl bg-white border-2 border-black/5 p-8 text-center" data-testid="my-courses-empty">
                <p className="text-black/70 font-medium">{t("dashboard.noEnrollments")}</p>
                <Link
                  to="/courses"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-3 shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
                >
                  {t("dashboard.browseAll")}
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {rows.map((r) => (
                  <EnrollmentCard key={r.enrollment_id} row={r} lang={lang} t={t} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Certificates */}
        <section data-testid="my-certs-section">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight inline-flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-[#4C1D95]" />
            {t("certs.title")}
            {certs && certs.length > 0 && (
              <span className="rounded-full bg-black/5 text-black/70 font-bold text-sm px-2.5 py-0.5">
                {certs.length}
              </span>
            )}
          </h2>
          <div className="mt-4">
            {certs === null ? (
              <div className="h-24 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
            ) : certs.length === 0 ? (
              <div className="rounded-3xl bg-white border-2 border-black/5 p-6 text-black/60 font-medium" data-testid="certs-empty">
                {t("certs.empty")}
              </div>
            ) : (
              <ul className="grid md:grid-cols-2 gap-3">
                {certs.map((c) => (
                  <li
                    key={c.certificate_id}
                    className="rounded-3xl bg-gradient-to-br from-[#DFF6FD] to-[#EDE9FE] border-2 border-[#4C1D95]/20 p-5"
                    data-testid={`cert-card-${c.certificate_id}`}
                  >
                    <div className="text-[10px] font-black uppercase tracking-wider text-[#4C1D95]">
                      {c.score_percent}% · {new Date(c.issued_at).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")}
                    </div>
                    <div className="mt-1 font-extrabold text-[#1A1A1A]">
                      {lang === "ar" ? c.course_title_ar : c.course_title_en}
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${c.pdf_url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-[#1A1A1A] text-white font-bold px-3 py-1.5 text-xs shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
                        data-testid={`cert-dl-${c.certificate_id}`}
                      >
                        <Download className="h-3.5 w-3.5" /> {t("certs.download")}
                      </a>
                      <Link
                        to={`/verify/${c.certificate_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-3 py-1.5 text-xs transition"
                        data-testid={`cert-verify-${c.certificate_id}`}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" /> {t("certs.verify")}
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* My Orders */}
        <section data-testid="my-orders-section">
          <button
            onClick={() => setOrdersOpen((v) => !v)}
            className="w-full flex items-center justify-between text-start"
            data-testid="my-orders-toggle"
          >
            <h2 className="text-2xl md:text-3xl font-black tracking-tight inline-flex items-center gap-2">
              <Receipt className="h-6 w-6 text-black/40" />
              {t("dashboard.myOrders")}
              {orders && orders.length > 0 && (
                <span className="rounded-full bg-black/5 text-black/70 font-bold text-sm px-2.5 py-0.5">
                  {orders.length}
                </span>
              )}
            </h2>
            <span className="text-sm font-bold text-black/50">{ordersOpen ? "—" : "+"}</span>
          </button>
          {ordersOpen && (
            <div className="mt-4" data-testid="my-orders-list">
              {orders === null ? (
                <div className="h-20 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
              ) : orders.length === 0 ? (
                <div className="rounded-3xl bg-white border-2 border-black/5 p-6 text-black/60 font-medium">
                  {t("dashboard.noOrders")}
                </div>
              ) : (
                <ul className="space-y-2">
                  {orders.map((o) => (
                    <OrderRow key={o.order_id} o={o} t={t} lang={lang} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function OrderRow({ o, t, lang }) {
  const Icon =
    o.status === "succeeded" ? CheckCircle2 : o.status === "cancelled" || o.status === "failed" ? XCircle : Clock;
  const color =
    o.status === "succeeded" ? "text-[#166534]" : o.status === "cancelled" || o.status === "failed" ? "text-[#991B1B]" : "text-[#7A4B00]";
  const bg =
    o.status === "succeeded" ? "bg-[#DCFCE7]" : o.status === "cancelled" || o.status === "failed" ? "bg-[#FEE2E2]" : "bg-[#FEF3C7]";
  const title = (o.course_title && (o.course_title[lang] || o.course_title.en)) || o.course_id;
  return (
    <li
      className="rounded-2xl bg-white border-2 border-black/5 p-4 flex items-center gap-3"
      data-testid={`order-row-${o.order_id}`}
    >
      <span className={`inline-grid place-items-center h-9 w-9 rounded-full ${bg} ${color}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-extrabold text-sm text-[#1A1A1A] truncate">{title}</div>
        <div className="text-xs text-black/60 font-semibold">
          {o.scope === "full_course" ? "Full course" : `${(o.module_ids || []).length} module`} · {o.amount_display} ·{" "}
          <span className={color}>{t(`unlock.status_${o.status}`)}</span>
        </div>
      </div>
      <div className="text-[10px] font-mono text-black/40 hidden md:block">
        {t("unlock.orderId")}: {o.order_id.slice(0, 12)}
      </div>
    </li>
  );
}

function EnrollmentCard({ row, lang, t }) {
  const c = row.course;
  const p = row.progress || { percent_complete: 0, next_module_id: null };
  return (
    <div
      className="rounded-3xl bg-white border-2 border-black/5 overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all"
      data-testid={`enrollment-card-${c.course_id}`}
    >
      <div className="flex">
        <div className="w-28 grid place-items-center text-5xl" style={{ background: c.cover_gradient || "#4361EE" }}>
          <span>{c.cover_emoji || "✨"}</span>
        </div>
        <div className="flex-1 p-5">
          <div className="text-[10px] font-black uppercase tracking-wider text-black/50">{c.band}</div>
          <div className="mt-1 font-extrabold text-[#1A1A1A] truncate">{c.title[lang] || c.title.en}</div>
          <div className="mt-3 flex items-center justify-between text-xs font-bold text-black/70">
            <span>{p.completed_count}/{p.total_modules} · {p.percent_complete}%</span>
            {p.next_module_id && (
              <Link
                to={`/courses/${c.course_id}/modules/${p.next_module_id}`}
                className="inline-flex items-center gap-1 rounded-full bg-[#4361EE] text-white font-bold px-3 py-1.5 hover:bg-[#4361EE]/90 transition"
                data-testid={`continue-${c.course_id}`}
              >
                <PlayCircle className="h-3.5 w-3.5" /> {t("dashboard.continue")}
              </Link>
            )}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-black/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${p.percent_complete}%`, background: c.cover_gradient }} />
          </div>
        </div>
      </div>
    </div>
  );
}
